import AuthLayout from "../../layout/AuthLayout.jsx";
import AuthImage from "../../mosaic/images/auth-image.jpg";
import {useTranslation} from "react-i18next";
import FormControl from "../../components/form/FormControl.jsx";
import FormButton from "../../components/form/FormButton.jsx";
import {useContext, useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import InfoBox from "../../components/InfoBox.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import IdentityImage from "../../components/IdentityImage.jsx";
import {AuthenticationApi} from "../../api/authority/generated/index";
import FormError from "../../components/form/FormError.jsx";
import {DEFAULT_PROJECT_LOGO_URL} from "../userSettings/UserProjectList.jsx";

export default function ResetPassword() {

    let {t} = useTranslation();
    let navigate = useNavigate();

    let [searchParams, setSearchParams] = useSearchParams();
    let token = searchParams.get('token');

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    // -----------------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    let [showSuccessScreen, setShowSuccessScreen] = useState(false);

    // -----------------------------------------------------------------------------------------------------------------
    // load profile for current reset token:
    // -----------------------------------------------------------------------------------------------------------------

    let [tokenProfile, setTokenProfileDownloading, setTokenProfileResult, setTokenProfileError] = useDownloadState();

    useEffect(() => {

        if (!token) {
            return;
        }

        setTokenProfileDownloading(true);
        new AuthenticationApi(getAuthorityConfiguration())
            .checkResetToken({
                token: token
            })
            .then(r => setTokenProfileResult(r))
            .catch(e => setTokenProfileError(e))
            .finally(() => setTokenProfileDownloading(false));
    }, [token]);

    // -----------------------------------------------------------------------------------------------------------------
    // send form with email to receive link in mailbox...
    // -----------------------------------------------------------------------------------------------------------------

    let [initializeEmail, setInitializeEmail] = useState('');
    let [initializePressed, setInitializePressed] = useState(false);
    let [initializeState, setInitializeDownloading, setInitializeResult, setInitializeError] = useDownloadState();

    useEffect(() => {
        if (!initializePressed) {
            return;
        }

        setInitializePressed(false);

        setInitializeDownloading(true);

        new AuthenticationApi(getAuthorityConfiguration())
            .resetPasswordLink({
                resetPasswordSendLinkInput: {
                    email: initializeEmail
                }
            })
            .then(r => {
                setInitializeResult(r);
                setShowSuccessScreen(true);
            })
            .catch(e => setInitializeError(e))
            .finally(() => setInitializeDownloading(false));

    }, [initializePressed]);

    // -----------------------------------------------------------------------------------------------------------------
    // reset password with token and new password
    // -----------------------------------------------------------------------------------------------------------------

    /** @var {ResetPasswordInput} resetPasswordInput */
    let [resetPasswordInput, setResetPasswordInput] = useState(
        {
            token: token,
            newPassword: '',
            newPasswordAgain: '',
        }
    );

    let [resetPasswordPressed, setResetPasswordPressed] = useState(false);
    let [resetPasswordState, setResetPasswordDownloading, setResetPasswordResult, setResetPasswordError] = useDownloadState();

    useEffect(() => {
        if (!resetPasswordPressed) {
            return;
        }

        setResetPasswordPressed(false);

        if (resetPasswordInput.newPassword !== resetPasswordInput.newPasswordAgain) {
            setResetPasswordError({message: t('auth.passwords-must-match')});
            return;
        }

        setResetPasswordDownloading(true);

        new AuthenticationApi(getAuthorityConfiguration())
            .resetPassword({
                resetPasswordInput: resetPasswordInput
            })
            .then(r => {
                setResetPasswordResult(r);
                setShowSuccessScreen(true);
            })
            .catch(e => setResetPasswordError(e))
            .finally(() => setResetPasswordDownloading(false));

    }, [resetPasswordPressed]);

    // -----------------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------


    if (token) {
        // user came from the e-mail
        return (
            <AuthLayout title={t('auth.reset-password')} authImage={AuthImage}>

                {showSuccessScreen ?
                    <InfoBox type="success">
                        <div
                            className="font-medium text-slate-800 dark:text-slate-100 mb-1">{t('auth.password-changed')}</div>
                        <span
                            className={'font-medium text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer'}
                            onClick={() => navigate('/sign/in')}
                        >
                            {t('auth.continue-to-login')}
                        </span>
                    </InfoBox>
                    :
                    <LoadingComponent loading={tokenProfile.downloading || resetPasswordState.downloading}>
                        {tokenProfile.error && <FormError error={tokenProfile.error} className={'mt-6'}/>}

                        {!tokenProfile.error &&
                            <>
                                <FormControl
                                    label={t('auth.reset-password-token')}
                                    type={'text'}
                                    value={token}
                                    disabled={true}
                                />

                                <div className={'mt-6 flex gap-2'}>
                                    <IdentityImage url={tokenProfile.result?.avatarUrl ?? DEFAULT_PROJECT_LOGO_URL}/>

                                    <div className="flex items-center truncate">
                                    <span
                                        className="truncate mx-2 text-sm font-medium dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-slate-200">
                                        {tokenProfile.result?.firstName ?? ''}
                                        &nbsp;
                                        {tokenProfile.result?.lastName ?? ''}
                                    </span>
                                    </div>
                                </div>

                                {resetPasswordState.error &&
                                    <FormError error={resetPasswordState.error} className={'mt-6'}/>}

                                <FormControl
                                    className={'mt-6'}
                                    label={t('auth.password')}
                                    type={'password'}
                                    value={resetPasswordInput.newPassword}
                                    onChange={(e) => setResetPasswordInput({
                                        ...resetPasswordInput,
                                        newPassword: e.target.value
                                    })}
                                />

                                <FormControl
                                    label={t('auth.password-again')}
                                    type={'password'}
                                    value={resetPasswordInput.newPasswordAgain}
                                    onChange={(e) => setResetPasswordInput({
                                        ...resetPasswordInput,
                                        newPasswordAgain: e.target.value
                                    })}
                                />

                                <div className="auth-btn-wrap">
                                    <FormButton className={'button-primary mt-3'}
                                                onClick={() => setResetPasswordPressed(true)}>
                                        {t('auth.create-new-password')}
                                    </FormButton>
                                </div>
                            </>
                        }

                    </LoadingComponent>
                }

            </AuthLayout>
        );
    }


    return (
        <AuthLayout title={t('auth.reset-password')} authImage={AuthImage}>
            {showSuccessScreen ?
                <InfoBox type="success">
                    <div
                        className="font-medium text-slate-800 dark:text-slate-100 mb-1">{t('auth.reset-email-sent')}</div>
                    <div>{t('auth.reset-email-notice')}</div>
                </InfoBox>
                :
                <LoadingComponent loading={initializeState.downloading}>
                    {initializeState.error && <FormError error={initializeState.error} className={'mt-6'}/>}

                    <FormControl
                        label={t('auth.email-address')}
                        type={'email'}
                        value={initializeEmail}
                        onChange={(e) => setInitializeEmail(e.target.value)}
                    />

                    <div className="auth-btn-wrap">
                        <FormButton className={'button-primary mt-3'} onClick={() => setInitializePressed(true)}>
                            {t('auth.send-password-reset-link')}
                        </FormButton>
                    </div>

                </LoadingComponent>
            }
        </AuthLayout>
    );
}
